/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import platform from 'platform';
import { HOME_URL } from './constants.ts';

export class Util {
  static clamp(val: number, min: number, max: number) {
    return Math.max(Math.min(val, max), min);
  }

  static generateProjectLink(uid: string, title: string, callback: () => void) {
    const url = HOME_URL + '?userid=' + uid + '&project=' + encodeURIComponent(title);
    navigator.clipboard.writeText(url).then(callback);
  }

  static isOpenFromURL() {
    const params = new URLSearchParams(window.location.search);
    const userid = params.get('userid');
    const project = params.get('project');
    return !!(userid && project);
  }

  static getOS(): string | undefined {
    return platform.os?.family;
  }

  static isMac(): boolean {
    const os = Util.getOS();
    if (os) return os.includes('Mac') || os.includes('OS X');
    return false;
  }

  static isChrome(): boolean {
    const os = Util.getOS();
    if (os) return os.includes('Chrome');
    return false;
  }

  static getCelsius(kevin: number): number {
    return kevin - 273.15;
  }

  static getKevin(celsius: number): number {
    return celsius + 273.15;
  }

  static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  static toDegrees(radians: number): number {
    return radians * (180 / Math.PI);
  }

  static decimalColorToRgb(value: number) {
    return Util.hexColorToRgb(value.toString(16));
  }

  static hexColorToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  static nearestNiceNumber(positiveNumber: number): number {
    // get the first larger power of 10
    let nice = Math.pow(10, Math.ceil(Math.log10(positiveNumber)));
    // scale the power to a "nice enough" value
    if (positiveNumber < 0.25 * nice) nice = 0.25 * nice;
    else if (positiveNumber < 0.5 * nice) nice = 0.5 * nice;
    return nice;
  }

  static capitalizeFirstLetter(s: string): string {
    if (s.length === 1) return s.toUpperCase();
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }

  static isNumericAndWhitespaceOnly(str: string) {
    return /^[\d\s]*$/.test(str);
  }

  static findFirstAlphabeticalCharacterIndex(str: string) {
    const regex = /[a-zA-Z]/; // Regular expression to match any letter (a-z or A-Z)
    return str.search(regex); // Returns the index of the first match, or -1 if no match
  }

  // https://en.wikipedia.org/wiki/Chemical_table_file
  static correctSdf(input: string) {
    const lines = input.split('\n');
    // V3000 has no fixed columns, so there is nothing to repair — and the
    // V2000 counts / bond-line rewriting below would corrupt it. Pass through.
    if (lines.length > 3 && /V3000/i.test(lines[3])) return input;
    let result = '';
    let lineIndex = 0;
    let stop = false;
    let atomNumber = 0;
    let bondNumber = 0;
    let atomCount = 0;
    let bondCount = 0;
    let firstBond = false;
    const atomCoordinates = [];
    let bondCountPosition = -1;
    for (const a of lines) {
      if (a.trim() === '$$$$') break; // we do not support multiple molecules in a single SDF for now
      if (a.trim() === 'M  END') stop = true;
      if (stop) {
        // no need to do anything with other info
        result += a + '\n';
      } else {
        if (lineIndex < 3) {
          // header has three lines: title line, program line, and comment line
          result += a + '\n';
        } else if (lineIndex === 3) {
          // Counts line. V2000 is fixed-width: atom count in columns 1-3 and
          // bond count in columns 4-6, both right-justified (the SDFParser
          // reads substr(0, 3) and substr(3, 3)). AI-written files often space
          // the fields instead, and a well-formed file with ≥ 100 atoms and
          // ≥ 100 bonds runs the two together ("120119"). Parse either way and
          // always emit the padded form — the earlier per-width branches wrote
          // " 50102" as " 50 102" (read as 10 bonds: a 95-atom / 102-bond
          // rifamycin lost most of its bonds) and "  8 13" as "  8  13".
          const c1 = a.substr(0, 3).trim();
          const c2 = a.substr(3, 3).trim();
          let tail: string[];
          if (/^\d+$/.test(c1) && /^\d+$/.test(c2) && (a.length <= 6 || a[6] === ' ')) {
            // Fixed-width as written (" 60117  0 …", "120119  0 …"): read the
            // columns — trimming first would turn " 60117" into 601 / 17.
            atomNumber = parseInt(c1);
            bondNumber = parseInt(c2);
            tail = a.substr(6).trim().split(/\s+/).filter(Boolean);
          } else {
            const s = a.trim().split(/\s+/);
            if (/^\d{4,6}$/.test(s[0])) {
              // run-together counts with the leading space lost: "aaabbb"
              atomNumber = parseInt(s[0].substring(0, 3));
              bondNumber = parseInt(s[0].substring(3, 6));
              tail = s.slice(1);
            } else {
              atomNumber = parseInt(s[0]);
              bondNumber = parseInt(s[1]);
              tail = s.slice(2);
            }
          }
          // The bond count is rewritten at the end with the bonds actually kept.
          bondCountPosition = result.length + 3;
          // Remaining fields, three columns each; a run-together numeric token
          // ("0999" = "  0" + "999") is split from the right.
          const field = (t: string): string => {
            if (/^V[23]000$/i.test(t)) return ' ' + t.toUpperCase();
            if (/^\d{4,}$/.test(t)) return field(t.slice(0, -3)) + t.slice(-3);
            return t.padStart(3, ' ');
          };
          const rest = tail.map(field).join('');
          result += String(atomNumber).padStart(3, ' ') + String(bondNumber).padStart(3, ' ') + rest + '\n';
        } else {
          if (Util.isNumericAndWhitespaceOnly(a)) {
            if (!firstBond) {
              firstBond = true;
              // just add more lines to validate sdf if the actual atom count is less than the specified number of atoms
              // so that users have a visual cue that shows the sdf is incorrect
              if (atomCount < atomNumber) {
                result += '    0.0000    0.0000    0.0000  X  0  0  0  0  0  0  0  0  0  0\n'.repeat(
                  atomNumber - atomCount,
                );
              }
            }
            // bond block: V2000 spec mandates fixed-width fields — cols 1-3
            // atom1, 4-6 atom2, 7-9 type. Whitespace-split breaks for any
            // bond where both atom indices have 3 digits (e.g. "100101  1"
            // collapses to a single token "100101"), so 100+ atom polymers
            // had every backbone bond past atom 99 silently dropped. Read
            // the fixed-width slice first; if it doesn't validate as two
            // numerics, fall back to whitespace split for malformed AI-
            // generated SDFs that omitted the column padding.
            let b1 = '';
            let b2 = '';
            let b3 = '';
            if (a.length >= 9) {
              const f1 = a.substr(0, 3).trim();
              const f2 = a.substr(3, 3).trim();
              const f3 = a.substr(6, 3).trim();
              // All three fields must read as numbers for the line to count as
              // fixed-width: a space-separated "100 101 1 0" also yields numeric
              // "100" / "10" in the first two slots, but its third slot is "1 1".
              if (/^\d+$/.test(f1) && /^\d+$/.test(f2) && /^\d+$/.test(f3)) {
                b1 = f1;
                b2 = f2;
                b3 = f3;
              }
            }
            if (!b1 || !b2) {
              const ss = a.trim().split(/\s+/);
              if (ss.length >= 2) {
                b1 = ss[0];
                b2 = ss[1];
                b3 = ss.length >= 3 ? ss[2] : '';
              }
            }
            if (b1 && b2) {
              const k1 = parseInt(b1) - 1; // SDF bond index starts from 1
              const k2 = parseInt(b2) - 1;
              if (k1 >= 0 && k2 >= 0 && k1 < atomCoordinates.length && k2 < atomCoordinates.length) {
                const dx = atomCoordinates[k1][0] - atomCoordinates[k2][0];
                const dy = atomCoordinates[k1][1] - atomCoordinates[k2][1];
                const dz = atomCoordinates[k1][2] - atomCoordinates[k2][2];
                // only keep bonds shorter than 5 angstrom
                if (dx * dx + dy * dy + dz * dz < 25) {
                  if (b1.length === 1) {
                    result += '  ' + b1;
                  } else if (b1.length === 2) {
                    result += ' ' + b1;
                  } else {
                    result += b1;
                  }
                  if (b2.length === 1) {
                    result += '  ' + b2;
                  } else if (b2.length === 2) {
                    result += ' ' + b2;
                  } else {
                    result += b2;
                  }
                  if (b3) {
                    if (b3.length === 1) {
                      result += '  ' + b3;
                    } else if (b3.length === 2) {
                      result += ' ' + b3;
                    } else {
                      result += b3;
                    }
                  }
                  result += '\n';
                  bondCount++;
                }
              }
            }
          } else {
            // atom block: three space indent or spacing if negative and four space indent or spacing if positive
            const s = a.trim();
            const n = Util.findFirstAlphabeticalCharacterIndex(s);
            const s1 = s.substring(0, n).trim();
            const s2 = s.substring(n);
            const c1 = s1.split(/\s+/);
            const v = [];
            // Each coordinate goes into its V2000 field: 10 characters, right-
            // justified, exactly 4 decimals. It used to be rebuilt as "integer
            // part . decimals" with the decimals padded but never cut, so any
            // value not written with exactly 4 decimals shifted every later
            // column — 6-decimal coordinates read back as y = 0, z = 7200 and
            // element "692"; an integer "2" (no decimal point) became ".2000",
            // and "-1" became NaN. Formatting the number itself fixes all three.
            for (let i = 0; i < c1.length; i++) {
              const x = c1[i].trim();
              const f = parseFloat(x);
              const val = Number.isFinite(f) ? f : 0;
              result += val.toFixed(4).padStart(10, ' ');
              if (i < 3) v.push(val);
            }
            atomCoordinates.push(v);
            // the element symbol is expected to start from position 31
            const d2 = s2.split(/\s+/);
            for (let i = 0; i < d2.length; i++) {
              const x = d2[i].trim();
              if (x === '') continue;
              if (i === 0) {
                // this is the element symbol
                if (x.length === 1) {
                  // if the element symbol has one letter
                  result += ' ' + x + '  ';
                } else {
                  // if the element symbol has two letters
                  result += ' ' + x + ' ';
                }
              } else {
                // other data of the atom are separated by two spaces
                result += x + '  ';
              }
            }
            result += '\n';
            atomCount++;
          }
        }
        lineIndex++;
      }
    }
    // Write the number of bonds actually kept into columns 4-6 of the counts
    // line (bonds longer than 5 Å were dropped above). bondCountPosition is
    // -1 when the input had no counts line at all; the old unconditional
    // substring(0, -1) then pasted the count over the first two characters
    // of the title line.
    if (bondCountPosition >= 0) {
      const r1 = result.substring(0, bondCountPosition);
      const r2 = result.substring(bondCountPosition + 3);
      result = r1 + String(bondCount).padStart(3, ' ') + r2;
    }
    result = result.trim();
    if (!result.endsWith('$$$$')) result += '\n\n$$$$';
    // console.log(result);
    return result;
  }

  static getSubscriptNumber(s: string): string {
    return s
      .replace(/1/g, '₁')
      .replace(/2/g, '₂')
      .replace(/3/g, '₃')
      .replace(/4/g, '₄')
      .replace(/5/g, '₅')
      .replace(/6/g, '₆')
      .replace(/7/g, '₇')
      .replace(/8/g, '₈')
      .replace(/9/g, '₉')
      .replace(/0/g, '₀');
  }
}
